import debounce from 'lodash/debounce';
import React, {
  cloneElement,
  PropsWithChildren,
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  NativeSyntheticEvent,
  NativeScrollEvent,
  Platform,
  ScrollView,
  StyleProp,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';

import useCssScrollSnap from '../../hooks/use-css-scroll-snap';
import generateID from '../../utils/generate-id';

type TabProp = PropsWithChildren<{
  /**
   * The Tab's label
   */
  label: string;
  /**
   * Customize component styles
   */
  style?: StyleProp<ViewStyle>;
  accessibilityLabel?: string;
  /**
   * If Tabs is provided an id this will be automatically generated
   * but you can still customize it to your liking.
   */
  id?: string;
}>;

const Tab = (props: TabProp) => {
  const viewRef = useRef<View>(null);

  useEffect(() => {
    if (Platform.OS === 'web') {
      viewRef.current?.setNativeProps({
        className: 'rnw-scroll-snap-item',
      });
    }
  }, []);

  return (
    <View
      style={props.style}
      accessibilityLabel={props.accessibilityLabel}
      /** Generate HTML and Test IDs  */
      {...generateID(props.id)}
      ref={viewRef}
    >
      {props.children}
    </View>
  );
};

type TabsProp = {
  activeTabIndex: number;
  /**
   * Invoked when the tab is changed (either by scrolling or pressing the tabs)
   */
  onTabChange?: (tabIndex: number) => void;
  /**
   * Tab
   */
  children: ReactElement<TabProp> | ReactElement<TabProp>[];
  /**
   * Customize component styles
   */
  style?: StyleProp<ViewStyle>;
  /**
   * Customize the scrolling viewport's styles
   */
  viewPortStyle?: StyleProp<ViewStyle>;
  accessibilityLabel?: string;
  viewPortAccessbilityLabel?: string;
  id?: string;
};

const Tabs = (props: TabsProp) => {
  useCssScrollSnap();
  const [tabs, setTabs] = useState<TabProp[]>([]);
  const [tabWidth, setTabWidth] = useState(0);
  const scrollViewRef = useRef<ScrollView>(null);

  const children = props.children;

  /**
   * Only for Web
   * Sets custom CSS rules to enable scroll snapping
   */
  useEffect(() => {
    if (Platform.OS === 'web' && document) {
      scrollViewRef.current?.setNativeProps({
        className: 'rnw-scroll-snap-container',
      });
    }
  }, []);

  /** Set the Tabs' labels */
  useEffect(() => {
    if (Array.isArray(children)) {
      setTabs(children.map((tab) => ({ label: tab.props.label })));
    } else {
      setTabs([{ label: children.props.label }]);
    }
  }, [children]);

  const onTabChange = (tabIndex: number) => {
    return () => props.onTabChange?.(tabIndex);
  };

  const scrollToTab = (tabIndex: number) => {
    return () => {
      if (tabWidth) {
        scrollViewRef.current?.scrollTo({
          x: tabWidth * tabIndex,
          animated: false,
        });
      }
    };
  };

  useEffect(scrollToTab(props.activeTabIndex), [
    props.activeTabIndex,
    tabWidth,
  ]);

  const onScroll = useCallback(
    debounce(
      (event: NativeSyntheticEvent<NativeScrollEvent>) => {
        const offset = event.nativeEvent.contentOffset.x;
        // This function most probably only be useful on Web
        // As the ScrollView supports snapping on mobile
        // And this implementation may have issues with responsive
        const tabIndex = Math.round(offset / tabWidth);

        if (props.activeTabIndex !== tabIndex) {
          props.onTabChange?.(tabIndex);
        }
      },
      128,
      { leading: false, trailing: true }
    ),
    [props.activeTabIndex, tabWidth]
  );

  return (
    <View
      accessibilityLabel={props.accessibilityLabel}
      /** Generate HTML and Test IDs  */
      {...generateID(props.id)}
    >
      <View
        style={[
          style.header,
          tabs.length === 1 ? { padding: 0, maxWidth: 100 } : { padding: 4 },
        ]}
      >
        {tabs.map((tab, index) => {
          const isActive = props.activeTabIndex === index;

          return (
            <TouchableOpacity
              key={index}
              onPress={onTabChange(index)}
              style={[style.headerItem, isActive && style.headerItemActive]}
              /** Generate HTML and Test IDs  */
              {...generateID(props.id && `${props.id}-header-${index}`)}
            >
              <Text
                style={[
                  style.headerItemText,
                  isActive && style.headerItemTextActive,
                ]}
              >
                {tab.label}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>
      <ScrollView
        accessibilityLabel={props.viewPortAccessbilityLabel}
        ref={scrollViewRef}
        style={[style.scrollView, props.style]}
        contentContainerStyle={props.viewPortStyle}
        onLayout={(event) => setTabWidth(event.nativeEvent.layout.width)}
        horizontal
        scrollEventThrottle={1}
        onScroll={onScroll}
        /** Generate HTML and Test IDs  */
        {...generateID(props.id && `${props.id}-viewport`)}
      >
        {Array.isArray(children)
          ? children.map((child, index) =>
              cloneElement(child, {
                accessibilityLabel: child.props.accessibilityLabel,
                style: [child.props.style, { width: tabWidth || '100%' }],
                /** Generate HTML and Test IDs  */
                id: child.props.id || (props.id && `${props.id}-item-${index}`),
                key: child.key,
              })
            )
          : { children }}
      </ScrollView>
    </View>
  );
};

const style = StyleSheet.create({
  header: {
    flexDirection: 'row',
    marginBottom: 16,
    borderRadius: 100,
    backgroundColor: '#e1e1e1',
    justifyContent: 'space-evenly',
    height: 40,
  },
  headerItem: {
    flex: 1,
    paddingVertical: 4,
    borderRadius: 100,
    justifyContent: 'center',
  },
  headerItemActive: {
    backgroundColor: '#fff',
  },
  headerItemText: {
    fontSize: 14,
    lineHeight: 22,
    textAlign: 'center',
  },
  headerItemTextActive: {
    fontWeight: 'bold',
  },
  scrollView: {
    flexDirection: 'row',
  },
});

export default Tabs;
export { Tab };
